import React, { useEffect, useRef } from 'react';
import { embedDashboard } from '@superset-ui/embedded-sdk';

interface IProps {
  embedUuid: string;
  host: string;
  /**
   * Returns a fresh, RLS-scoped Superset guest token. The SDK calls this on
   * mount and on token expiry, so it must fetch a new token each time.
   */
  fetchGuestToken: () => Promise<string>;
  onError?: (error: unknown) => void;
}

/**
 * Renders a Superset dashboard via the embedded SDK for the admin preview.
 *
 * NOTE: This is intentionally a copy of the client app's
 * app/src/app/containers/EmbeddedReporting/renderers/SupersetEmbed.tsx — admin
 * can't import across the package boundary, mirroring how the admin viewer also
 * inlines <tableau-viz> rather than sharing TableauEmbed. Both should be
 * extracted to a shared library (e.g. alongside @revfluence/ui-studio) later.
 */
export const SupersetEmbed: React.FC<IProps> = ({ embedUuid, host, fetchGuestToken, onError }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const fetchGuestTokenRef = useRef(fetchGuestToken);
  fetchGuestTokenRef.current = fetchGuestToken;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    if (!containerRef.current || !embedUuid || !host) {
      return undefined;
    }

    let cancelled = false;
    let unmount: (() => void) | undefined;

    embedDashboard({
      id: embedUuid,
      supersetDomain: host,
      mountPoint: containerRef.current,
      fetchGuestToken: () => fetchGuestTokenRef.current(),
      dashboardUiConfig: {
        hideTitle: true,
        hideTab: true,
        hideChartControls: false,
        filters: { visible: true, expanded: false },
      },
    })
      .then((instance) => {
        if (cancelled) {
          instance?.unmount?.();
          return;
        }
        unmount = instance?.unmount;
        // The SDK injects the iframe with no dimensions, so it collapses to the
        // browser default (~300x150) inside our full-size container. Make it
        // fill the mount point so the dashboard is actually visible.
        const iframe = containerRef.current?.querySelector('iframe');
        if (iframe) {
          iframe.style.width = '100%';
          iframe.style.height = '100%';
          iframe.style.border = '0';
        }
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        // eslint-disable-next-line no-console
        console.warn('SupersetEmbed: embedDashboard failed', error);
        onErrorRef.current?.(error);
      });

    return () => {
      cancelled = true;
      if (unmount) {
        unmount();
      }
    };
  }, [embedUuid, host]);

  return <div ref={containerRef} style={{ width: '100%', height: '100%' }} />;
};

SupersetEmbed.displayName = 'SupersetEmbed';
